/**
 * ノート編集画面の状態機械。層1（ドラフト）と層2（自動保存）の合流点。
 *
 * ここが担うのは 3 つだけ:
 *  1. ノートを開くときに「ヘッドとドラフトのどちらを見せるか」を決める
 *     （判定は共有の純粋関数 `decideDraftRestore` に委ねる）
 *  2. 孤児ドラフトをサーバー履歴へ退避してから捨てる（何も失わない）
 *  3. NoteSaver を起動し、その後の保存を任せる
 *
 * UI はこのフックが返す `content` と `status` を表示するだけにする。
 * **エディタの本文は React state で制御しない**（再レンダリングが composition を壊すため）。
 * `content.body` は初期値と保存対象としてのみ使い、入力中の反映には使わない。
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@app/api/client';
import {
  attachDraftCreateAttemptIfUnchanged,
  deleteDraft,
  deleteDraftIfUnchanged,
  finalizeRecoveredDraft,
  getDraft,
  isLocalNoteId,
  newLocalNoteId,
  noteToDraftContent,
  promoteDraftAfterCreate,
  type DraftCreateAttempt,
  type DraftRecord,
} from '@app/storage/draft-buffer';
import { SESSION_ID } from '@app/storage/session-id';
import { beginEditorOpenOwnership } from '@app/save/editor-open-ownership';
import { tryAcquireDraftRecoveryOwnership } from '@app/save/recovery-ownership';
import { NoteSaver, type NoteContent, type SaveStatus } from '@app/save/note-saver';
import { useToast } from '@app/ui/toast';
import { decideDraftRestore, shouldCreateNewNote } from '@shared/save-protocol';
import { todayJst } from '@shared/date';
import type { Note } from '@shared/types';

export type EditorLoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export interface NoteEditorHandle {
  load: EditorLoadState;
  /** 現在のノート ID。新規作成中は `local:` 接頭辞のローカル一時 ID。 */
  noteId: string;
  /** サーバー上のヘッド（新規作成中は null）。 */
  note: Note | null;
  /** エディタの初期値・保存対象。入力中の値の反映には使わない。 */
  initialContent: NoteContent;
  status: SaveStatus;
  /** ドラフトから復元されて未保存の状態で開いたか。 */
  restoredFromDraft: boolean;
  /** 本文以外（タイトル・対象日・カテゴリ）の更新。 */
  update: (patch: Partial<NoteContent>) => void;
  /** 本文の更新。エディタから composition 中でも呼んでよい。 */
  updateBody: (body: string) => void;
  /** エディタの composition 状態を問い合わせる関数を登録する。 */
  bindComposing: (isComposing: () => boolean) => void;
  /** composition 終了をセーバーへ伝える。 */
  notifyCompositionEnd: () => void;
  /** 今すぐ保存を試みる。 */
  flush: () => Promise<void>;
  retryNow: () => void;
  /**
   * サーバー側でこのノートが書き換えられた後（切り出しなど）に、読み直す。
   *
   * 保留中の保存は**送らずに捨てる**。この時点のローカル内容はサーバーの変更を
   * 知らない古いものなので、送るとサーバー側の変更を巻き戻す。ローカルドラフトも
   * 同じ理由で削除する（残すと次に開いたとき古い本文が復元されてしまう）。
   */
  reloadAfterServerEdit: () => Promise<void>;
  /**
   * サーバーから読み直すたびに増える世代番号。
   *
   * エディタの作り直しは**これを key にすることでは行っていない**。読み直しの間は
   * `load` が `'loading'` に戻り、編集画面が丸ごとアンマウントされる。復帰時に
   * 新しい `initialContent` で新規マウントされるため、結果として本文が差し替わる。
   * この値は effect の依存としての用途（＝読み直しの起動）が本体。
   *
   * **`load` の扱いを変えるときは注意する。** 読み直し中にアンマウントされなくなると、
   * `Editor` は memo で完全に隔離されている（ADR-017）ため本文が古いまま残る。
   */
  contentKey: number;
}

function emptyContent(): NoteContent {
  return {
    title: null,
    body: '',
    category_id: null,
    // 未操作なら作成日（JST）。入力摩擦をゼロにするための既定（ADR-005）。
    subject_date: todayJst(),
    subject_date_end: null,
  };
}

function contentOf(note: Note): NoteContent {
  return {
    title: note.title,
    body: note.body,
    category_id: note.category_id,
    subject_date: note.subject_date,
    subject_date_end: note.subject_date_end,
  };
}

export function useNoteEditor(routeNoteId: string | undefined): NoteEditorHandle {
  const toast = useToast();
  const [load, setLoad] = useState<EditorLoadState>({ kind: 'loading' });
  const [note, setNote] = useState<Note | null>(null);
  const [initialContent, setInitialContent] = useState<NoteContent>(emptyContent);
  const [status, setStatus] = useState<SaveStatus>('saved');
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);
  const [noteId, setNoteId] = useState<string>(() => routeNoteId ?? newLocalNoteId());

  const [contentKey, setContentKey] = useState(0);

  const saverRef = useRef<NoteSaver | null>(null);
  const composingRef = useRef<() => boolean>(() => false);
  /** 次のクリーンアップで flush せずに捨てるか（サーバー側編集後の読み直し）。 */
  const abandonOnCleanupRef = useRef(false);

  const bindComposing = useCallback((fn: () => boolean) => {
    composingRef.current = fn;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let effectSaver: NoteSaver | null = null;
    const localId = routeNoteId ?? newLocalNoteId();
    // 既存ノートは GET / draft 復旧より前から所有する。回収スイープが open 途中の
    // draft を送信・削除する隙を作らず、成功時は同じ lease を NoteSaver へ渡す。
    const openOwnership = routeNoteId ? beginEditorOpenOwnership(routeNoteId) : null;
    setLoad({ kind: 'loading' });
    setRestoredFromDraft(false);

    async function open(): Promise<void> {
      // --- 新規ノート ---
      // サーバーへの POST は初回自動保存まで遅らせる。起動〜入力開始の間に
      // ネットワーク往復を挟まないため（docs/01-requirements.md §5.1）。
      if (!routeNoteId) {
        const content = emptyContent();
        if (cancelled) return;
        setNoteId(localId);
        setNote(null);
        setInitialContent(content);
        effectSaver = new NoteSaver({
          noteId: localId,
          baseVersion: 0,
          content,
          syncedContent: null,
          isComposing: () => composingRef.current(),
          events: makeEvents(),
        });
        saverRef.current = effectSaver;
        setLoad({ kind: 'ready' });
        return;
      }

      // --- 既存ノート ---
      const [head, draft] = await Promise.all([
        api.getNote(routeNoteId),
        getDraft(routeNoteId).catch(() => undefined),
      ]);
      if (cancelled) return;

      // 同期済み（dirty=false）の古いキャッシュは復元対象ではない。他端末でヘッドが
      // 進んだだけの状態を「未送信分」と誤認して履歴へ退避しない。
      const decision = decideDraftRestore(draft?.dirty ? draft : null, head);
      let content = contentOf(head);
      let dirty = false;

      if (decision.kind === 'restore_draft' && draft) {
        // 黙って復元し、未保存としてマークする（ユーザーに判断を求めない）。
        content = {
          title: draft.title,
          body: draft.body,
          category_id: draft.category_id,
          subject_date: draft.subject_date,
          subject_date_end: draft.subject_date_end,
        };
        dirty = true;
      } else if (decision.kind === 'rescue_orphan_draft' && draft) {
        // 他端末が先行している。ドラフトを適用するとヘッドを巻き戻してしまうので、
        // ヘッドを表示しつつ、ドラフトは履歴へ退避してから捨てる。何も失わない。
        try {
          await api.createRevision(routeNoteId, {
            title: draft.title,
            body: draft.body,
            category_id: draft.category_id,
            subject_date: draft.subject_date,
            subject_date_end: draft.subject_date_end,
            version: draft.base_version,
            session_id: draft.session_id,
            reason: 'draft_recovery',
          });
          if (cancelled) return;
          await deleteDraftIfUnchanged(draft);
          if (!cancelled) {
            toast.show('この端末の未保存分を履歴へ退避しました', {
              label: '履歴を見る',
              run: () => {
                window.location.href = `/notes/${routeNoteId}/revisions`;
              },
            });
          }
        } catch {
          // 退避に失敗したらドラフトを消さない。次回の起動で再挑戦する。
          if (!cancelled) {
            toast.show('未保存分の退避に失敗しました。この端末に保持しています');
          }
        }
      }

      if (cancelled) return;
      setNoteId(routeNoteId);
      setNote(head);
      setInitialContent(content);
      const ownership = openOwnership!.takeForSaver();
      try {
        effectSaver = new NoteSaver({
          noteId: routeNoteId,
          // restore_draft はドラフトの base_version がヘッド以上のときにしか返らないので、
          // ヘッドの版番号を基準にしてよい。
          baseVersion: head.version,
          content,
          // サーバーが持っているのは常にヘッドの内容。これと content の差分が dirty。
          syncedContent: contentOf(head),
          isComposing: () => composingRef.current(),
          events: makeEvents(),
          ownership,
        });
      } catch (error) {
        ownership.release();
        throw error;
      }
      saverRef.current = effectSaver;
      setRestoredFromDraft(dirty);
      setStatus(dirty ? 'pending' : 'saved');
      setLoad({ kind: 'ready' });
      if (dirty) {
        // 復元直後に落ちても内容が残るよう、ドラフトはそのまま保持する。
        // 保存は通常の debounce ではなく即座に試みる。
        void saverRef.current.flush();
      }
    }

    function makeEvents() {
      return {
        onStatusChange: (next: SaveStatus) => {
          if (!cancelled) setStatus(next);
        },
        onNoteIdAssigned: (serverId: string, created: Note) => {
          if (cancelled) return;
          setNoteId(serverId);
          setNote(created);
          // URL を新しいノートへ差し替える。履歴を増やさない（戻るで空の新規に戻らない）。
          window.history.replaceState(null, '', `/notes/${serverId}/edit`);
        },
        onRegression: () => {
          if (cancelled) return;
          // 競合解決ダイアログは出さない。事後報告のみ（ADR-003）。
          toast.show('以前の内容を履歴に退避しました', {
            label: '履歴を見る',
            run: () => {
              window.location.href = `/notes/${saverRef.current?.noteId ?? ''}/revisions`;
            },
          });
        },
        onUnauthorized: () => {
          if (cancelled) return;
          toast.show('ログインの有効期限が切れました。再ログインしてください', {
            label: 'ログイン',
            run: () => {
              window.location.href = api.loginUrl();
            },
          });
        },
        onTrashedConflict: () => {
          if (cancelled) return;
          toast.show('このノートはゴミ箱にあります。復元してから編集してください');
        },
      };
    }

    open().catch((error: unknown) => {
      openOwnership?.cancel();
      if (cancelled) return;
      if (error instanceof ApiError && error.code === 'not_found') {
        setLoad({ kind: 'not_found' });
        return;
      }
      const message = error instanceof Error ? error.message : '不明なエラー';
      setLoad({ kind: 'error', message });
    });

    return () => {
      cancelled = true;
      openOwnership?.cancel();
      const saver = effectSaver;
      if (saverRef.current === saver) saverRef.current = null;
      if (abandonOnCleanupRef.current) {
        // サーバー側でこのノートが書き換えられた直後。古い内容を送ると
        // サーバーの変更を巻き戻すので、送らずに捨てる。
        abandonOnCleanupRef.current = false;
        saver?.abandon();
      } else {
        // 画面を離れるときは、保留中の内容を送り切ってから終える。
        void saver?.dispose();
      }
    };
    // toast は ToastProvider が useMemo で固定しているので再実行の原因にならない。
  }, [routeNoteId, toast, contentKey]);

  // タブが隠れる・閉じられるときに取りこぼしをなくす。
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void saverRef.current?.flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
    };
  }, []);

  const update = useCallback((patch: Partial<NoteContent>) => {
    saverRef.current?.update(patch);
  }, []);

  const updateBody = useCallback((body: string) => {
    saverRef.current?.update({ body });
  }, []);

  const notifyCompositionEnd = useCallback(() => {
    saverRef.current?.notifyCompositionEnd();
  }, []);

  const flush = useCallback(async () => {
    await saverRef.current?.flush();
  }, []);

  const retryNow = useCallback(() => {
    saverRef.current?.retryNow();
  }, []);

  const reloadAfterServerEdit = useCallback(async () => {
    abandonOnCleanupRef.current = true;
    // 古い本文のドラフトを残すと、次に開いたときにサーバーの変更を巻き戻す形で
    // 復元されてしまう。サーバーが正になったので捨てる。
    if (!isLocalNoteId(noteId)) {
      await deleteDraft(noteId).catch(() => undefined);
    }
    // contentKey を進めると effect が再実行され、サーバーから読み直される。
    setContentKey((n) => n + 1);
  }, [noteId]);

  return {
    load,
    noteId,
    note,
    initialContent,
    status,
    restoredFromDraft,
    update,
    updateBody,
    bindComposing,
    notifyCompositionEnd,
    flush,
    retryNow,
    reloadAfterServerEdit,
    contentKey,
  };
}

/**
 * 起動時の復旧: 前回の起動で送り切れなかったドラフトを掃き出す。
 *
 * 編集画面を開かなくても未送信分がサーバーへ届くようにするためのもの。
 * ローカル一時 ID のドラフト（＝サーバーに実体がない新規ノート）も POST して救出する。
 */
export async function recoverPendingDrafts(): Promise<number> {
  const { listDirtyDrafts } = await import('@app/storage/draft-buffer');
  const drafts = await listDirtyDrafts().catch(() => []);
  let recovered = 0;

  for (const draft of drafts) {
    // 回収側も一時 lease を持つ。API 待機中に editor が開かれたら排他性が失われるため、
    // 応答を draft へ適用せず新しい saver に譲る。
    const recoveryOwnership = tryAcquireDraftRecoveryOwnership(draft.note_id);
    if (!recoveryOwnership) continue;
    let serverRecoveryOwnership: ReturnType<typeof tryAcquireDraftRecoveryOwnership> = null;
    try {
      if (isLocalNoteId(draft.note_id)) {
        let localDraftCurrent: DraftRecord | undefined = draft;
        let createAttempt: DraftCreateAttempt | undefined;

        // marker付与または空削除が競合したら最新値を1回だけ読み直し、
        // marker → 非空 → 空の順へ戻る。古いsnapshotの汎用putは行わない。
        for (let casAttempt = 0; casAttempt < 2 && localDraftCurrent; casAttempt++) {
          if (!recoveryOwnership.isExclusive()) {
            localDraftCurrent = undefined;
            break;
          }
          const proposedAttempt: DraftCreateAttempt | undefined =
            localDraftCurrent.create_attempt ??
            (shouldCreateNewNote(localDraftCurrent.title, localDraftCurrent.body)
              ? {
                  body: localDraftCurrent.body,
                  title: localDraftCurrent.title,
                  category_id: localDraftCurrent.category_id,
                  subject_date: localDraftCurrent.subject_date,
                  subject_date_end: localDraftCurrent.subject_date_end,
                  // 初回POSTを実行する現在タブのsessionを使い、T-006以前の作成契約を維持する。
                  session_id: SESSION_ID,
                  client_request_id: localDraftCurrent.note_id,
                }
              : undefined);

          if (proposedAttempt) {
            const attached = await attachDraftCreateAttemptIfUnchanged(
              localDraftCurrent,
              proposedAttempt,
            );
            if (!recoveryOwnership.isExclusive()) {
              localDraftCurrent = undefined;
              break;
            }
            if (attached.kind !== 'conflict') {
              localDraftCurrent = attached.draft;
              createAttempt = attached.draft.create_attempt;
              break;
            }
          } else if (await deleteDraftIfUnchanged(localDraftCurrent)) {
            localDraftCurrent = undefined;
            break;
          } else if (!recoveryOwnership.isExclusive()) {
            localDraftCurrent = undefined;
            break;
          }

          if (casAttempt >= 1) {
            localDraftCurrent = undefined;
            break;
          }
          const latest = await getDraft(draft.note_id);
          if (!recoveryOwnership.isExclusive() || !latest) {
            localDraftCurrent = undefined;
            break;
          }
          localDraftCurrent = latest;
        }
        if (!localDraftCurrent || !createAttempt) continue;

        const created = await api.createNote(createAttempt);
        if (!recoveryOwnership.isExclusive()) continue;
        // local と server の両IDを同時に保護してから昇格する。server editor が既に
        // 開いていれば、その saver に任せて回収側は適用しない。
        serverRecoveryOwnership = tryAcquireDraftRecoveryOwnership(created.id);
        if (!serverRecoveryOwnership) continue;
        const serverSnapshot = {
          ...localDraftCurrent,
          ...noteToDraftContent(created),
          base_version: created.version,
          create_attempt: createAttempt,
        };
        const promoted = await promoteDraftAfterCreate(
          serverSnapshot,
          created.id,
          created.version,
        );
        if (
          !promoted ||
          !recoveryOwnership.isExclusive() ||
          !serverRecoveryOwnership.isExclusive()
        ) {
          continue;
        }

        const current = await getDraft(created.id);
        if (
          !current ||
          !recoveryOwnership.isExclusive() ||
          !serverRecoveryOwnership.isExclusive()
        ) {
          continue;
        }
        if (!current.dirty) {
          recovered++;
          continue;
        }

        // 冪等POSTが既存本文Aを返し、localに追加入力Bがある場合は、同じスイープ内で
        // Bをserver IDへPUTする。次のonline等の外部イベントへ依存させない。
        const result = await api.updateNote(created.id, {
          body: current.body,
          title: current.title,
          category_id: current.category_id,
          subject_date: current.subject_date,
          subject_date_end: current.subject_date_end,
          base_version: current.base_version,
          session_id: current.session_id,
        });
        if (
          !recoveryOwnership.isExclusive() ||
          !serverRecoveryOwnership.isExclusive()
        ) {
          continue;
        }
        if (await finalizeRecoveredDraft(current, result.note.version)) recovered++;
        continue;
      }

      const head = await api.getNote(draft.note_id);
      // GET の待ち時間にエディタが開かれた場合も、回収側から PUT しない。
      if (!recoveryOwnership.isExclusive()) continue;
      const decision = decideDraftRestore(draft, head);
      if (decision.kind === 'discard_draft') {
        await finalizeRecoveredDraft(draft, head.version);
        continue;
      }
      if (decision.kind === 'rescue_orphan_draft') {
        await api.createRevision(draft.note_id, {
          title: draft.title,
          body: draft.body,
          category_id: draft.category_id,
          subject_date: draft.subject_date,
          subject_date_end: draft.subject_date_end,
          version: draft.base_version,
          session_id: draft.session_id,
          reason: 'draft_recovery',
        });
        if (!recoveryOwnership.isExclusive()) continue;
        if (await deleteDraftIfUnchanged(draft)) recovered++;
        continue;
      }
      const result = await api.updateNote(draft.note_id, {
        body: draft.body,
        title: draft.title,
        category_id: draft.category_id,
        subject_date: draft.subject_date,
        subject_date_end: draft.subject_date_end,
        base_version: draft.base_version,
        session_id: draft.session_id,
      });
      if (!recoveryOwnership.isExclusive()) continue;
      if (await finalizeRecoveredDraft(draft, result.note.version)) recovered++;
    } catch {
      // 失敗しても捨てない。次回の起動で再試行する。
    } finally {
      serverRecoveryOwnership?.release();
      recoveryOwnership.release();
    }
  }
  return recovered;
}
